import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

/**
 * HelpFab — floating "?" button that shows the 3 most relevant FAQ
 * items for the current route in a popover. Clicking the popover header
 * navigates to the full Help Centre for that module.
 */

// Map frontend paths to backend help module slugs.
const ROUTE_TO_SLUG: Array<[RegExp, string]> = [
  [/^\/crm/,            "crm"],
  [/^\/whatsapp/,       "whatsapp"],
  [/^\/voice-sales/,    "voicesales"],
  [/^\/omnivoice/,      "omnivoice"],
  [/^\/email/,          "emailintel"],
  [/^\/inventory/,      "inventory"],
  [/^\/marketplace/,    "marketplace"],
  [/^\/accounting/,     "accounting"],
  [/^\/shopify/,        "accounting"],
  [/^\/profit/,         "accounting"],
  [/^\/commission/,     "commission"],
  [/^\/seo/,            "seo"],
  [/^\/market-intel/,   "marketintel"],
  [/^\/content/,        "content"],
  [/^\/agents/,         "agentshub"],
  [/^\/automation/,     "agentshub"],
  [/^\/hr/,             "hr"],
  [/^\/attendance/,     "attendance"],
];

function slugForPath(pathname: string): string | null {
  for (const [re, slug] of ROUTE_TO_SLUG) {
    if (re.test(pathname)) return slug;
  }
  return null;
}

interface Article {
  id: string;
  module_slug: string;
  title: string;
  body: string;
  kind: string;
}

const HelpFab: React.FC = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [open, setOpen]         = useState(false);
  const [items, setItems]       = useState<Article[]>([]);
  const [loading, setLoading]   = useState(false);
  const [locale, setLocale]     = useState<"en" | "ar">(
    typeof navigator !== "undefined" && /^ar\b/i.test(navigator.language || "") ? "ar" : "en",
  );
  const popRef                  = useRef<HTMLDivElement>(null);

  const slug = slugForPath(pathname);

  // Hide entirely on /help itself
  const shouldRender = pathname !== "/help" && !pathname.startsWith("/help/");

  // Close when clicking outside
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (popRef.current && !popRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // Fetch relevant FAQ when opening
  useEffect(() => {
    if (!open || !slug) return;
    setLoading(true);
    fetch(`/api/help/articles?module=${slug}&locale=${locale}&kind=faq&limit=3`, {
      credentials: "include",
    })
      .then(r => r.ok ? r.json() : [])
      .then(data => setItems(Array.isArray(data) ? data.slice(0, 3) : []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [open, slug, locale]);

  if (!shouldRender) return null;

  const isRTL = locale === "ar";

  return (
    <>
      {/* FAB */}
      <button
        onClick={() => setOpen(o => !o)}
        title={locale === "en" ? "Quick help" : "مساعدة سريعة"}
        style={{
          position: "fixed",
          bottom: 28,
          right: isRTL ? "auto" : 28,
          left: isRTL ? 28 : "auto",
          width: 48,
          height: 48,
          borderRadius: "50%",
          background: "linear-gradient(135deg, #bdb2ff, #ffd6a5)",
          color: "#060e20",
          border: "none",
          cursor: "pointer",
          boxShadow: "0 4px 20px rgba(189,178,255,0.4)",
          fontFamily: "'Manrope', sans-serif",
          fontSize: 22,
          fontWeight: 800,
          zIndex: 9998,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        ?
      </button>

      {open && (
        <div
          ref={popRef}
          dir={isRTL ? "rtl" : "ltr"}
          style={{
            position: "fixed",
            bottom: 86,
            right: isRTL ? "auto" : 28,
            left: isRTL ? 28 : "auto",
            width: 340,
            maxHeight: "70vh",
            overflowY: "auto",
            background: "rgba(6,14,32,0.94)",
            backdropFilter: "blur(60px)",
            WebkitBackdropFilter: "blur(60px)",
            border: "0.5px solid rgba(189,178,255,0.2)",
            borderRadius: 14,
            padding: 14,
            color: "#dde5ff",
            fontFamily: "'Space Grotesk', sans-serif",
            zIndex: 9999,
            boxShadow: "0 8px 40px rgba(0,0,0,0.5)",
          }}
        >
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            marginBottom: 10, gap: 8,
          }}>
            <div
              style={{
                color: "#bdb2ff", fontSize: 13, fontWeight: 700, cursor: "pointer",
                fontFamily: "'Manrope',sans-serif",
              }}
              onClick={() => { setOpen(false); navigate("/help"); }}
              title={locale === "en" ? "Open Help Centre" : "افتح مركز المساعدة"}
            >
              {locale === "en" ? "Quick Help →" : "مساعدة سريعة ←"}
            </div>
            <div style={{ display: "flex", gap: 2 }}>
              {(["en", "ar"] as const).map(l => (
                <button
                  key={l}
                  onClick={() => setLocale(l)}
                  style={{
                    fontSize: 10, padding: "2px 6px", borderRadius: 5, border: "none", cursor: "pointer",
                    background: locale === l ? "#bdb2ff" : "transparent",
                    color: locale === l ? "#060e20" : "#6d758b",
                    fontWeight: 700,
                  }}
                >
                  {l.toUpperCase()}
                </button>
              ))}
            </div>
          </div>

          {!slug && (
            <div style={{ color: "#6d758b", fontSize: 12 }}>
              {locale === "en"
                ? "No module-specific help for this page. Open the Help Centre for full docs."
                : "لا توجد مساعدة خاصة لهذه الصفحة. افتح مركز المساعدة للاطلاع على التوثيق الكامل."}
            </div>
          )}

          {slug && loading && (
            <div style={{ color: "#6d758b", fontSize: 12, padding: 8 }}>
              {locale === "en" ? "Loading…" : "جارٍ التحميل…"}
            </div>
          )}

          {slug && !loading && items.length === 0 && (
            <div style={{ color: "#6d758b", fontSize: 12 }}>
              {locale === "en" ? "No FAQ yet for this module." : "لا توجد أسئلة شائعة بعد."}
            </div>
          )}

          {slug && !loading && items.map(it => (
            <details
              key={it.id}
              style={{
                marginBottom: 8,
                background: "rgba(189,178,255,0.06)",
                border: "0.5px solid rgba(189,178,255,0.15)",
                borderRadius: 10,
                padding: "8px 10px",
              }}
            >
              <summary style={{
                cursor: "pointer", fontSize: 12, fontWeight: 600, color: "#dde5ff",
              }}>
                {it.title}
              </summary>
              <div style={{
                marginTop: 6, fontSize: 12, lineHeight: 1.5,
                color: "rgba(221,229,255,0.85)", whiteSpace: "pre-wrap",
              }}>
                {it.body}
              </div>
            </details>
          ))}
        </div>
      )}
    </>
  );
};

export default HelpFab;
